import { ProductionPerformanceReportModule } from "../../production-reports/ProductionPerformanceReportModule";
import { operatorEventDuration } from "../operator-status-view";
import { amount } from "../operator/quantity-format";
import { sumKnownQuantities } from "../../../../shared/production/quantity";
import type { SupervisorBoardOptions, SupervisorBoardWorkspace } from "./useSupervisorBoardWorkspace";

export function ProductionManagerLiveReport({ workspace, notify, dataRefreshVersion }: {
  workspace: SupervisorBoardWorkspace;
  notify: SupervisorBoardOptions["notify"];
  dataRefreshVersion: number;
}) {
  const { activeShiftCode, managerReportView, setManagerReportView, operatorReport } = workspace;
  const { shiftReadyCount, summaryScrap, summaryProduction, summaryPlanned, summaryUnit, summaryRemaining, shiftStoppedCount, shiftActiveCount, managerAvailability, managerScrapRate, summaryDowntime, managementFocus, managerLineMetrics, lossReasons, largestLossMinutes, dataQualityIssues } = workspace.model;
  return (
    <section className="production-management-live" aria-label="Canlı üretim performansı">
      <header>
        <div>
          <h2>Üretim özeti</h2>
          <small>{activeShiftCode} · Veriler 15 saniyede yenilenir</small>
        </div>
        <span className={`management-live-status${shiftStoppedCount ? " is-critical" : ""}`}>
          <i />
          {shiftStoppedCount ? `${shiftStoppedCount} hat duruşta` : `${shiftActiveCount} hat üretimde`}
        </span>
      </header>
      <nav className="management-report-tabs" aria-label="Üretim raporları">
        {([
          ["overview", "Genel görünüm"],
          ["plan", "Plan ve gerçekleşen"],
          ["losses", "Kayıp analizi"],
          ["operators", "Operatör performansı"],
          ["data-quality", "Veri kalitesi"],
        ] as const).map(([view, label]) => (
          <button
            type="button"
            className={managerReportView === view ? "is-active" : ""}
            aria-current={managerReportView === view ? "page" : undefined}
            onClick={() => setManagerReportView(view)}
            key={view}
          >{label}</button>
        ))}
      </nav>
      {managerReportView === "overview" ? <><div className="management-kpis">
        <article className={summaryProduction != null && summaryPlanned != null && summaryProduction < summaryPlanned ? "is-warning" : ""}>
          <small>ÜRETİM / PLAN</small>
          <strong>{amount(summaryProduction, summaryUnit)} <i>/ {amount(summaryPlanned, summaryUnit)}</i></strong>
          <span>{summaryProduction == null || summaryPlanned == null ? "Birimler ayrı incelenmeli / veri doğrulanmalı" : summaryProduction >= summaryPlanned ? `${amount(summaryProduction - summaryPlanned, summaryUnit)} plan üstü` : `${amount(summaryRemaining, summaryUnit)} kaldı`}</span>
        </article>
        <article className={shiftStoppedCount ? "is-critical" : ""}>
          <small>HAT DURUMU</small>
          <strong>{shiftActiveCount} çalışıyor <i>· {shiftStoppedCount} duruşta</i></strong>
          <span>{shiftReadyCount ? `${shiftReadyCount} iş başlamayı bekliyor` : "Bekleyen iş yok"}</span>
        </article>
        <article className={summaryDowntime > 30 ? "is-critical" : summaryDowntime ? "is-warning" : ""}>
          <small>DURUŞ KAYBI</small>
          <strong>{summaryDowntime} dk</strong>
          <span>{managerAvailability == null ? "Kayıtlı çalışma süresi yok" : `%${managerAvailability.toFixed(1).replace(".", ",")} kullanılabilirlik`}</span>
        </article>
        <article className={managerScrapRate != null && managerScrapRate > 3 ? "is-critical" : ""}>
          <small>ISKARTA</small>
          <strong>{managerScrapRate == null ? "—" : `%${managerScrapRate.toFixed(1).replace(".", ",")}`}</strong>
          <span>{amount(summaryScrap, summaryUnit)} toplam kayıp</span>
        </article>
      </div>
        {managementFocus.length ? <section className="management-focus" aria-label="Yönetim odağı">
          <header>
            <div><h3>Dikkat gerekenler</h3></div>
            <span>{managementFocus.length} önemli sinyal</span>
          </header>
          <div>
            {managementFocus.map((item) => (
              <article className={item.status === "Kritik" ? "is-critical" : "is-warning"} key={item.lineId}>
                <header><span>{item.status}</span><small>{item.lineName}</small></header>
                <strong>{item.primaryLoss}</strong>
                <p>{item.consequence}</p>
                <footer><span>Vardiya sonu</span><b>{item.projectedAttainment == null ? "Hesaplanamıyor" : `%${Math.round(item.projectedAttainment)}`}</b></footer>
              </article>
            ))}
          </div>
        </section> : null}</> : null}
      {managerReportView === "overview" ? <section className="management-comparison" aria-label="Hat performans karşılaştırması">
        <header>
          <div><h3>Hatlar</h3></div>
        </header>
        <div className="management-line-performance">
          <header>
            <span>Hat / durum</span>
            <span>Plan / gerçek</span>
            <span>Tahmin</span>
            <span>Performans</span>
            <span>Ana kayıp / durum</span>
          </header>
          {managerLineMetrics.map((line) => (
            <article className={line.severity >= 3 ? "is-critical" : line.severity ? "is-warning" : ""} key={line.lineId}>
              <span className="management-line-name"><strong>{line.lineName}</strong><small>{line.stopped ? "Duruşta" : line.active ? "Üretimde" : "Bekliyor"}</small></span>
              <span className="management-plan-actual"><b>{amount(line.planned, line.unit)}</b><small>{amount(line.production, line.unit)} gerçekleşen</small></span>
              <span className="management-forecast"><b>{line.projectedAttainment == null ? "—" : `%${Math.round(line.projectedAttainment)}`}</b><small>{amount(line.projectedProduction, line.unit)}</small></span>
              <span className="management-performance-cell"><b>{line.speedState === "below-target" || line.speedState === "on-target" ? `%${Math.round(line.speed.performance ?? 0)}` : "—"}</b>{line.speedState === "below-target" || line.speedState === "on-target" ? <i><em style={{ width: `${Math.min(100, line.speed.performance ?? 0)}%` }} /></i> : <small>{line.performanceLabel}</small>}</span>
              <span className="management-primary-loss"><b>{line.primaryLoss}</b><small>{line.signal}</small></span>
            </article>
          ))}
          {!managerLineMetrics.length ? <p>Bu vardiyada ölçülebilir üretim verisi yok.</p> : null}
        </div>
      </section> : null}
      {managerReportView === "plan" ? <ProductionPerformanceReportModule embedded notify={notify} dataRefreshVersion={dataRefreshVersion} /> : null}
      {managerReportView === "losses" ? <section className="management-losses" aria-label="Duruş kaybı nedenleri">
        <header><div><h3>Duruş nedenleri</h3></div><strong>{summaryDowntime} dk toplam</strong></header>
        {lossReasons.length ? <div>{lossReasons.map((item) => <article key={item.name}><span><strong>{item.name}</strong><small>{item.count} duruş · {operatorEventDuration(item.milliseconds)}</small></span><i><em style={{ width: `${(item.minutes / largestLossMinutes) * 100}%` }} /></i></article>)}</div> : <p>Bu vardiyada kaydedilmiş duruş kaybı yok.</p>}
      </section> : null}
      {managerReportView === "operators" ? <section className="management-operator-performance" aria-label="Operatör performansı">
        <header><div><h3>Fiili operatör performansı</h3><small>Terminal oturumu ve hareketi yapan operatör esas alınır; iş emri ataması değildir.</small></div><strong>{operatorReport.length} operatör</strong></header>
        {operatorReport.length ? <div className="management-operator-performance-table"><header><span>Operatör</span><span>Oturum</span><span>Üretim</span><span>Iskarta / fire</span><span>Duruş</span></header>{operatorReport.map((item) => <article key={item.operatorId ?? item.operatorName}><span><strong>{item.operatorName}</strong><small>{item.runCount} işte hareket</small></span><b>{item.sessionMinutes} dk</b><b>{item.quantityGroups.length ? item.quantityGroups.map((group) => <span style={{ display: "block" }} key={group.unit}>{amount(group.production, group.unit)}</span>) : "—"}</b><b>{item.quantityGroups.length ? item.quantityGroups.map((group) => <span style={{ display: "block" }} key={group.unit}>{amount(sumKnownQuantities([group.scrap, group.waste]), group.unit)}</span>) : "—"}{item.unknownUnitEntryCount ? <small>{item.unknownUnitEntryCount} kaydın dönüşümü belirsiz</small> : null}</b><b>{operatorEventDuration(item.downtimeMinutes * 60_000)}{item.shortStopCount ? <small>{item.shortStopCount} kısa duruş · {operatorEventDuration(item.shortStopSeconds * 1000)}</small> : null}</b></article>)}</div> : <p>Bu operasyon tarihinde fiili operatör hareketi bulunmuyor.</p>}
      </section> : null}
      {managerReportView === "data-quality" ? <section className="management-data-quality" aria-label="Veri kalitesi kontrolleri">
        <header><div><h3>Veri kalitesi</h3><small>Yanlış veya eksik rapor üretme riski taşıyan kayıtlar</small></div><strong>{dataQualityIssues.reduce((sum, item) => sum + item.count, 0)} kontrol</strong></header>
        {dataQualityIssues.length ? <div>{dataQualityIssues.map((item) => <article key={item.label}><span><b>{item.count}</b></span><div><strong>{item.label}</strong><small>{item.detail}</small></div></article>)}</div> : <p>Bu vardiyada raporlamayı etkileyen veri eksiği bulunmuyor.</p>}
      </section> : null}
    </section>
  );
}
